import { useEffect, useState } from "react";
import { X, Ticket as TicketIcon, Clock, CheckCircle2, Activity } from "lucide-react";
import AdminShell, { Panel, Btn, StatusPill } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { FilterChips } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const STATUS_FILTERS = [{ id: "", label: "All" }, { id: "open", label: "Open" }, { id: "in_progress", label: "In Progress" }, { id: "resolved", label: "Resolved" }, { id: "closed", label: "Closed" }];

export default function SupportAdmin() {
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const pager = useAdminPager();
  const [listTotal, setListTotal] = useState(0);

  async function load() {
    const { data } = await api.get("/api/admin/support/tickets", {
      params: {
        skip: pager.skip,
        limit: pager.limit,
        ...(statusFilter ? { status: statusFilter } : {}),
      },
    });
    const { items, total } = unwrapAdminList(data, "tickets");
    setTickets(asAdminList(items));
    pager.setTotal(total);
    setListTotal(total);
  }
  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, pager.page]);

  async function openTicket(t) {
    const { data } = await api.get(`/api/admin/support/tickets/${t.id}`);
    setOpen(data);
  }
  async function send() {
    if (!reply.trim()) return;
    await api.post(`/api/admin/support/tickets/${open.id}/reply`, { body: reply });
    setReply("");
    openTicket(open);
    load();
  }
  async function setStatus(s) {
    await api.patch(`/api/admin/support/tickets/${open.id}`, { status: s });
    openTicket(open);
    load();
  }

  const ticketRows = asAdminList(tickets);
  const todayResolved = ticketRows.filter((t) => t.status === "resolved" && new Date(t.updated_at).toDateString() === new Date().toDateString()).length;

  return (
    <AdminShell title="Support & Disputes">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Open Tickets" value={ticketRows.filter((t) => t.status === "open").length} icon={TicketIcon} variant="yellow" />
        <KpiCard label="In Progress" value={ticketRows.filter((t) => t.status === "in_progress").length} icon={Activity} variant="blue" />
        <KpiCard label="Resolved Today" value={todayResolved} icon={CheckCircle2} variant="green" />
        <KpiCard label="Avg Response Time" value="2.4 hrs" delta="-15min" deltaLabel="vs last week" icon={Clock} variant="purple" />
      </div>

      <Panel>
        <div className="p-4 border-b border-[#1E2845] flex justify-between items-center gap-3">
          <h3 className="text-[14px] font-bold">Tickets ({listTotal})</h3>
          <FilterChips options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} testIdPrefix="ticket-status" />
        </div>
        <DataTable
          testid="tickets-table"
          columns={[
            { key: "subject", header: "Subject", render: (t) => <span className="font-bold">{t.subject}</span> },
            { key: "user", header: "User", render: (t) => <span className="text-[#8892A8]">{t.user_email}</span> },
            { key: "category", header: "Category", render: (t) => <span className="text-[#8892A8] capitalize">{t.category || "—"}</span> },
            { key: "priority", header: "Priority", render: (t) => <StatusPill value={t.priority} /> },
            { key: "status", header: "Status", render: (t) => <StatusPill value={t.status} /> },
            { key: "created_at", header: "Created", render: (t) => <span className="text-[#8892A8] text-[11px]">{new Date(t.created_at).toLocaleString()}</span> },
            { key: "actions", header: "", align: "right", render: (t) => <button onClick={() => openTicket(t)} className="text-[#F0B83A] hover:underline text-[12px] font-bold" data-testid={`open-${t.id}`}>Open</button> },
          ]}
          rows={ticketRows}
          rowKey={(t) => t.id}
          empty="No tickets yet."
        />
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          onPage={pager.setPage}
          testId="tickets-pager"
        />
      </Panel>

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0F1626] border border-[#1E2845] rounded-[14px] w-full max-w-[680px] max-h-[90vh] flex flex-col" data-testid="ticket-modal">
            <div className="p-5 border-b border-[#1E2845] flex justify-between">
              <div>
                <h3 className="text-[16px] font-bold">{open.subject}</h3>
                <div className="text-[11px] text-[#8892A8] mt-0.5">{open.user_email}</div>
                <div className="flex gap-2 mt-1.5"><StatusPill value={open.priority} /><StatusPill value={open.status} /></div>
              </div>
              <button onClick={() => setOpen(null)} className="text-[#8892A8]"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {open.messages?.map((m, i) => (
                <div key={i} className={`p-3 rounded-[10px] ${m.from === "admin" ? "bg-[#F0B83A]/10 border border-[#F0B83A]/20 ml-12" : "bg-[#1A2138] mr-12"}`}>
                  <div className="text-[10px] uppercase font-bold text-[#8892A8]">{m.from} · {new Date(m.at).toLocaleString()}</div>
                  <div className="text-[13px] mt-1 whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-[#1E2845] flex flex-col gap-2">
              <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply..." data-testid="ticket-reply" className="w-full p-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]" />
              <div className="flex gap-2 flex-wrap">
                <Btn onClick={send} data-testid="ticket-send">Send reply</Btn>
                <Btn kind="ghost" onClick={() => setStatus("in_progress")}>In progress</Btn>
                <Btn kind="success" onClick={() => setStatus("resolved")} data-testid="ticket-resolve">Resolve</Btn>
                <Btn kind="ghost" onClick={() => setStatus("closed")}>Close</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
